import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, LogOut, Inbox, Trash2 } from "lucide-react";
import { Kicker } from "../components/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminEnquiries = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error();
        setUser(await res.json());
        setAuth(true);
      } catch {
        setAuth(false);
        navigate("/admin/login", { replace: true });
      }
    })();
  }, [location, navigate]);

  useEffect(() => {
    if (auth !== true) return;
    (async () => {
      const res = await fetch(`${API}/api/enquiries`, { credentials: "include" });
      if (res.ok) setEnquiries(await res.json());
    })();
  }, [auth]);

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/admin/login", { replace: true });
  };

  const removeEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    const res = await fetch(`${API}/api/enquiries/${id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) setEnquiries((prev) => prev.filter((e) => e.id !== id));
  };

  if (auth === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="enquiries-loading">
        <Loader2 size={28} className="animate-spin text-[#0A2B3D]" />
      </div>
    );
  }

  return (
    <div data-testid="enquiries-dashboard">
      <section className="bg-[#0A2B3D] text-white py-14">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <Kicker light>Team Area</Kicker>
            <h1 className="mt-3 font-serif font-bold text-3xl md:text-4xl tracking-tight">Enquiries</h1>
            <p className="mt-2 text-sm text-white/60" data-testid="enquiries-user">Signed in as {user?.name} ({user?.email})</p>
          </div>
          <button onClick={logout} data-testid="signout-button" className="btn-outline-light !py-2.5 !px-5 text-xs w-fit">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-[#EBF3F8] min-h-[50vh]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          {enquiries.length === 0 ? (
            <div className="bg-white border border-[#0A2B3D]/10 p-14 text-center" data-testid="enquiries-empty">
              <Inbox size={32} className="mx-auto text-[#3E9BDC]" />
              <p className="mt-4 font-serif text-xl text-[#0A2B3D]">No enquiries yet</p>
              <p className="mt-2 text-sm text-[#475569]">New consultation requests will appear here as they arrive.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4" data-testid="enquiries-list">
              {enquiries.map((e, i) => (
                <div key={e.id || i} className="bg-white border border-[#0A2B3D]/10 p-6 md:p-8" data-testid={`enquiry-card-${i}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-serif font-bold text-xl text-[#0A2B3D]">{e.name} — {e.firm}</h3>
                    <span className="flex items-center gap-4">
                      <span className="font-mono text-xs text-[#475569]">
                        {e.created_at ? new Date(e.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : ""}
                      </span>
                      <button
                        onClick={() => removeEnquiry(e.id)}
                        data-testid={`enquiry-delete-${i}`}
                        aria-label={`Delete enquiry from ${e.name}`}
                        className="text-[#475569] hover:text-red-600 transition-colors p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#475569]">
                    <span data-testid={`enquiry-topic-${i}`}><span className="font-semibold text-[#0A2B3D]">Topic:</span> {e.topic}</span>
                    {e.services?.length > 0 && <span><span className="font-semibold text-[#0A2B3D]">Services:</span> {e.services.join(", ")}</span>}
                    {e.role && <span><span className="font-semibold text-[#0A2B3D]">Role:</span> {e.role}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#475569]">
                    <a href={`mailto:${e.email}`} className="text-[#3E9BDC] hover:underline" data-testid={`enquiry-email-${i}`}>{e.email}</a>
                    {e.phone && <span>{e.phone}</span>}
                  </div>
                  {e.message && <p className="mt-4 text-sm text-[#475569] leading-relaxed border-t border-[#0A2B3D]/10 pt-4">{e.message}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminEnquiries;
